<template>
  <div class="profiles-section" ref="root">
    <div class="grid grid--container">
      <div class="profiles-section__content">
        <VText class="profiles-section__title" variant="h2">
          Join over 250k creators, agencies, and brands already on Campsite.bio
        </VText>
        <div class="profiles-section__text">
          <VText>
            Stay in control of your branding to create the bio link that matches
            you.
          </VText>
          <VText>
            The possibilities are endless with our profile customization
            features.
          </VText>
        </div>
        <Button color="secondary" variant="hard-shadow" :href="buttonUrl">
          Create Your Bio Link
        </Button>
      </div>
    </div>
    <transition-group
      name="previews"
      tag="div"
      class="profiles-section__profiles"
      ref="profilesContainer"
      appear
    >
      <template v-if="isVisible">
        <div
          class="profiles-section__profile"
          v-for="src in usernamesToShow"
          :key="src"
        >
          <DeviceIphone>
            <nuxt-img
              v-if="isClient"
              :src="src"
              loading="lazy"
              :sizes="`xs:300px`"
              style="width: 100%; height: 100%"
            />
          </DeviceIphone>
        </div>
      </template>
    </transition-group>
  </div>
</template>

<script>
import {
  VText,
  Button,
  useIsVisible,
  DeviceIphone,
} from '@campsite-bio/component-lib';
import { computed, ref, useContext } from '@nuxtjs/composition-api';
import shuffle from 'lodash/shuffle';
import { useMediaQuery, useResizeObserver } from '@vueuse/core';

const PROFILE_IMAGES = [
  '/campsites/creators/campsite-amybudden.png',
  '/campsites/creators/campsite-beccajamesuk.png',
  '/campsites/small-business/campsite-boltapp.png',
  '/campsites/small-business/campsite-camper.png',
  '/campsites/creators/campsite-cassierandolph.png',
  '/campsites/creators/campsite-chrisbethea.png',
  '/campsites/creators/campsite-copycompass.png',
  '/campsites/creators/campsite-choicehacking.png',
  '/campsites/business/campsite-dell.png',
  '/campsites/business/campsite-dellxps.png',
  '/campsites/creators/campsite-dothedamnthing.png',
  '/campsites/small-business/campsite-electrameccanica.png',
  '/campsites/creators/campsite-gisellemays.png',
  '/campsites/small-business/campsite-hometap.png',
  '/campsites/creators/campsite-jamiebareno.png',
  '/campsites/creators/campsite-heyjessica.png',
  '/campsites/creators/campsite-jon.png',
  '/campsites/creators/campsite-josh.png',
  '/campsites/small-business/campsite-justinboots.png',
  '/campsites/small-business/campsite-lightburn.png',
  '/campsites/creators/campsite-organicallybecca.png',
  '/campsites/business/campsite-participant.png',
  '/campsites/small-business/campsite-seeitalytravel.png',
  '/campsites/small-business/campsite-socialburro.png',
  '/campsites/non-profit/campsite-sundanceorg.png',
  '/campsites/small-business/campsite-telluridenews.png',
  '/campsites/creators/campsite-thewinestylings.png',
  '/campsites/small-business/campsite-topotoppers.png',
  '/campsites/education/campsite-miadcollege.png',
  '/campsites/creators/campsite-weratedogs.png',
  '/campsites/creators/campsite-alexokoroji.png',
  '/campsites/creators/campsite-mattevans.png',
  '/campsites/creators/campsite-adionnewilliams.png',
  '/campsites/small-business/campsite-lunasconsulting.png',
];

export default {
  components: {
    VText,
    DeviceIphone,
    Button,
  },

  props: {
    buttonUrl: {
      type: String,
      default: 'https://app.campsite.bio/create-account',
    },
  },

  setup() {
    const { $img, isDev } = useContext();
    const root = ref(null);
    const profilesContainer = ref(null);
    const profilesRect = ref(null);
    const isLargerScreen = useMediaQuery('(min-width: 768px)');

    const profileImagesGenerated = PROFILE_IMAGES.map((image) => {
      return isDev
        ? image
        : $img(image, { width: 300, quality: 80, format: 'webp' });
    });

    const profilesContainerEl = computed(() =>
      profilesContainer.value ? profilesContainer.value.$el : null,
    );

    const isVisible = useIsVisible(root, {
      runOnce: true,
    });

    useResizeObserver(profilesContainerEl, (entries) => {
      const entry = entries[0];
      profilesRect.value = entry.contentRect;
    });

    const usernamesToShow = computed(() => {
      if (!profilesRect.value) return [];

      const phoneWidth = 150;
      const { width } = profilesRect.value;
      const profileContainerWidth = isLargerScreen.value
        ? width
        : window.innerWidth;
      const columnSize = Math.ceil(profileContainerWidth / phoneWidth + 150);
      const rowSize = isLargerScreen.value ? 3 : 1;

      return shuffle(profileImagesGenerated).slice(0, columnSize * rowSize);
    });

    return {
      profilesContainer,
      usernamesToShow,
      root,
      isVisible,
      isClient: process.client,
    };
  },
};
</script>

<style lang="scss" scoped>
.profiles-section {
  background: var(--g-color-light-blue);
  box-shadow: inset 0px 4px 6px -3px rgba(0, 0, 0, 0.2),
    inset 0px -4px 6px -3px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  position: relative;
  text-align: center;

  @media (min-width: 992px) {
    text-align: left;
  }

  .grid {
    display: grid;
    padding-bottom: 250px;

    @media (min-width: 992px) {
      grid-template-columns: 1fr 1fr;
      gap: 5rem;
      padding-bottom: unset;
    }
  }

  &__content {
    padding-bottom: 3rem;
    padding-top: 4rem;
    position: relative;
    z-index: 1;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;

    @media (min-width: 992px) {
      padding-bottom: 13.5rem;
      padding-top: 12rem;
      max-width: 500px;
    }
  }

  &__text {
    margin-bottom: 1.65rem;

    p {
      font-size: 1.15rem;
      margin-bottom: 0.75em;

      @media (min-width: 768px) {
        font-size: 1.5rem;
      }
    }
  }

  &__profiles {
    display: flex;
    gap: 0.25rem;
    position: absolute;
    pointer-events: none;
    height: 303px;
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 53px);
    min-width: 458px;

    @media (min-width: 992px) {
      flex-wrap: wrap;
      bottom: unset;
      width: 60vw;
      height: 912px;
      left: 50vw;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  &__profile {
    position: relative;
    height: 303px;
    width: 150px;
    flex-shrink: 0;

    &::v-deep .device-iphone-x {
      transform: scale(0.35, 0.35);
      transform-origin: top left;
      position: absolute;
    }
  }
}

.profiles-section__title.profiles-section__title {
  margin-bottom: 1.5rem;
}

.previews-enter-active,
.previews-leave-active {
  transition: all var(--g-transition-speed);
}

.previews-enter,
.previews-leave-to {
  opacity: 0;
}
</style>
